<template>
  <div v-if="!ready" class="generator_loading">
    <div class="spinner-border text-secondary" role="status"></div>
  </div>

  <div v-else class="image_generator">
    <div class="preview_column">
      <div class="format_buttons">
        <WhiteButton
          v-for="format in formatOptions"
          :key="format.value"
          :class="{ active: editor.format.value === format.value }"
          @click="editor.format.value = format.value"
        >
          {{ format.label }}
        </WhiteButton>
      </div>

      <ImagePreview
        :format="editor.format.value"
        :config="editor.config.value"
        :data="editor.data.value"
        :pageName="article.site_name || 'Página'"
      />
      <small class="preview_size">{{ editor.formatInfo.value.width }} x {{ editor.formatInfo.value.height }} px</small>
    </div>

    <div class="options">
      <section class="option_group">
        <div class="option_header">
          <h6>Modelo</h6>
          <button
            type="button"
            class="default_toggle"
            :class="{ active: editor.isCurrentDefault.value }"
            :aria-pressed="editor.isCurrentDefault.value"
            @click="setDefault(!editor.isCurrentDefault.value)"
          >
            <i class="fa-star" :class="editor.isCurrentDefault.value ? 'fa-solid' : 'fa-regular'"></i>
            {{ editor.isCurrentDefault.value ? 'Modelo padrão' : 'Definir como padrão' }}
          </button>
        </div>

        <div class="templates_wrap" :class="{ scrolled: templatesScrolled }">
          <div class="templates" :class="editor.format.value" @scroll="onTemplatesScroll">
            <div
              v-for="option in editor.templateOptions.value"
              :key="option.source.type + (option.source.key || option.source.id)"
              class="template"
              :class="{ active: editor.isActive(option.source) }"
              role="button"
              tabindex="0"
              :aria-pressed="editor.isActive(option.source)"
              @click="editor.applyOption(option)"
              @keydown.enter="editor.applyOption(option)"
            >
              <TemplateThumb :format="option.format" :config="option.config" :data="editor.data.value" />
              <span v-if="editor.isDefault(option.source)" class="template_default" title="Modelo padrão">
                <i class="fa-solid fa-star"></i>
              </span>
              <button v-if="option.saved" type="button" class="template_delete" title="Excluir" @click.stop="templateToDelete = option.saved">
                <i class="fa-solid fa-trash"></i>
              </button>
              <span class="template_name">{{ option.name }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="option_group">
        <h6>Cores</h6>
        <div class="swatches">
          <button
            v-for="palette in PALETTES"
            :key="palette.key"
            type="button"
            class="swatch"
            :class="{ active: editor.config.value.palette === palette.key }"
            :title="palette.name"
            :aria-label="palette.name"
            :aria-pressed="editor.config.value.palette === palette.key"
            :style="backgroundStyle(palette.background)"
            @click="editor.applyPalette(palette)"
          >
            <span class="swatch_accent" :style="{ backgroundColor: palette.accent }"></span>
          </button>
          <label
            class="swatch custom"
            :class="{ active: editor.config.value.palette === 'custom' }"
            :style="editor.config.value.palette === 'custom' ? { backgroundColor: customColor } : null"
            title="Outra cor"
          >
            <span class="swatch_icon"><i class="fa-solid fa-eye-dropper"></i></span>
            <input type="color" :value="customColor" aria-label="Outra cor" @input="applyCustomColor($event.target.value)" />
          </label>
        </div>
      </section>

      <section class="option_group">
        <h6>Fonte</h6>
        <div class="fonts">
          <button
            v-for="pair in FONT_PAIRS"
            :key="pair.key"
            type="button"
            class="font"
            :class="{ active: editor.config.value.fontPair === pair.key }"
            :aria-pressed="editor.config.value.fontPair === pair.key"
            :title="pair.title.font"
            @click="editor.applyFontPair(pair)"
          >
            <span
              class="font_sample"
              :style="{ fontFamily: '\'' + pair.title.font + '\'', fontWeight: pair.title.weight, fontStyle: pair.title.italic ? 'italic' : 'normal' }"
            >Aa</span>
            <span class="font_name">{{ pair.name }}</span>
          </button>
        </div>
      </section>

      <section v-for="toggle in editor.toggles.value" :key="toggle.role" class="option_group">
        <h6>{{ toggle.label }}</h6>
        <div class="toggle_row">
          <InputSwitch
            :id="'toggle-' + toggle.role"
            :checked="toggle.element.hidden ? 0 : 1"
            @update:checked="editor.setToggle(toggle.role, !!$event)"
          />
          <InputText
            v-model="toggle.element.content"
            :name="'toggle-text-' + toggle.role"
            :disabled="toggle.element.hidden"
            maxlength="60"
            class="toggle_text"
          />
        </div>
      </section>
    </div>
  </div>

  <div v-if="ready" class="footer">
    <WhiteButton @click="openSave">Salvar modelo</WhiteButton>
    <div class="footer_actions">
      <PrimaryButton class="main_action" :disabled="downloading" @click="exportImage(canShare)">
        <template v-if="downloading">Gerando...</template>
        <template v-else-if="canShare">Compartilhar</template>
        <template v-else>Baixar imagem</template>
      </PrimaryButton>
      <WhiteButton v-if="canShare" class="save_image" :disabled="downloading" title="Salvar imagem" aria-label="Salvar imagem" @click="exportImage(false)">
        <i class="fa-solid fa-download"></i>
      </WhiteButton>
    </div>
  </div>

  <!--
    Fora do modal "Gerar imagem": dentro dele, o cabeçalho do modal pai
    (z-index alto no ModalDialog) ficaria por cima destes. A classe
    nested_modal garante que fiquem acima do modal pai.
  -->
  <Teleport to="#app">
    <ModalDialog title="Salvar modelo" width="400px" class="nested_modal" v-model="showSaveModal">
      <form @submit.prevent="confirmSave">
        <InputText v-model="templateName" name="template-name" label="Nome" maxlength="100" :error="saveError" />
        <PrimaryButton class="w-100 mt-4" :disabled="saving || !templateName.trim()">Salvar</PrimaryButton>
      </form>
    </ModalDialog>

    <ModalDialog title="Excluir modelo" width="400px" class="nested_modal" :modelValue="!!templateToDelete" @update:modelValue="templateToDelete = null">
      <p v-if="templateToDelete">Excluir "{{ templateToDelete.name }}"?</p>
      <div class="d-flex gap-2 mt-4">
        <WhiteButton class="w-100" @click="templateToDelete = null">Cancelar</WhiteButton>
        <PrimaryButton class="red w-100" @click="confirmDelete">Excluir</PrimaryButton>
      </div>
    </ModalDialog>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, defineProps } from 'vue';
import { useToast, TYPE } from 'vue-toastification';
import PrimaryButton from '@/components/PrimaryButton.vue';
import WhiteButton from '@/components/WhiteButton.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import InputSwitch from '@/components/InputSwitch.vue';
import InputText from '@/components/InputText.vue';
import ImagePreview from '@/components/image-generator/ImagePreview.vue';
import TemplateThumb from '@/components/image-generator/TemplateThumb.vue';
import { useImageEditor } from '@/image-generator/useImageEditor';
import { exportTemplate } from '@/image-generator/renderer';
import { loadFontStylesheet } from '@/image-generator/fonts';
import { FORMATS, PALETTES, FONT_PAIRS } from '@/image-generator/presets';

const props = defineProps({
  article: {
    type: Object,
    required: true,
  },
});

const toast = useToast();
const editor = useImageEditor(props.article);

const formatOptions = Object.values(FORMATS).map(format => ({ value: format.key, label: format.label }));

const ready = ref(editor.isLoaded());
const downloading = ref(false);
const saving = ref(false);
const customColor = ref('#583a8e');
const templatesScrolled = ref(false);
const showSaveModal = ref(false);
const templateName = ref('');
const saveError = ref(null);
const templateToDelete = ref(null);
/* A página de compartilhar já pré-carregou os modelos: abre direto, sem spinner. */
if (ready.value) {
  editor.applyInitialTemplates();
}

onMounted(async () => {
  loadFontStylesheet();

  if (!ready.value) {
    await editor.init();
    ready.value = true;
  }
});

function backgroundStyle(background) {
  if (background.type === 'gradient') {
    return { backgroundImage: 'linear-gradient(' + background.angle + 'deg, ' + background.colors[0] + ', ' + background.colors[1] + ')' };
  }

  return { backgroundColor: background.color };
}

function onTemplatesScroll(event) {
  templatesScrolled.value = event.target.scrollLeft > 0;
}

function applyCustomColor(color) {
  customColor.value = color;
  editor.applyCustomColor(color);
}

function slugify(text) {
  return (text || 'imagem')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 60);
}

/*
 * No celular, a imagem vai direto para a folha de compartilhamento do sistema
 * (Facebook, Threads...). No computador, ou se o navegador não suportar, baixa.
 */
const canShare = ref(detectShare());

function detectShare() {
  try {
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    const probe = new File([''], 'imagem.jpg', { type: 'image/jpeg' });

    return isTouch && !!navigator.canShare && navigator.canShare({ files: [probe] });
  } catch (error) {
    return false;
  }
}

function fileName() {
  return slugify(props.article.title) + '-' + editor.format.value + '.jpg';
}

function downloadBlob(blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function shareBlob(blob) {
  const file = new File([blob], fileName(), { type: 'image/jpeg' });

  try {
    await navigator.share({ files: [file] });
  } catch (error) {
    /* Cancelar a folha de compartilhamento não é erro. */
    if (error.name === 'AbortError') {
      return;
    }

    /* Sem permissão (gesto do usuário expirou etc.): baixa o arquivo. */
    downloadBlob(blob);
  }
}

/* share: abre a folha de compartilhamento; senão baixa o arquivo (botão de ícone no celular). */
async function exportImage(share) {
  downloading.value = true;

  try {
    const blob = await exportTemplate(editor.format.value, editor.config.value, editor.data.value);

    if (share) {
      await shareBlob(blob);
    } else {
      downloadBlob(blob);
    }
  } catch (error) {
    toast('Não foi possível gerar a imagem. Tente novamente', { type: TYPE.ERROR, timeout: 6000 });
  } finally {
    downloading.value = false;
  }
}

function openSave() {
  const saved = editor.savedTemplate.value;
  templateName.value = saved ? saved.name : '';
  saveError.value = null;
  showSaveModal.value = true;
}

async function confirmSave() {
  const name = templateName.value.trim();

  if (!name) {
    return;
  }

  saving.value = true;
  const result = await editor.save(name);
  saving.value = false;

  if (result.success) {
    showSaveModal.value = false;
    toast('Modelo salvo', { type: TYPE.SUCCESS, timeout: 3000 });
    return;
  }

  saveError.value = 'Não foi possível salvar o modelo';

  if (result.errors && result.errors.name) {
    saveError.value = result.errors.name[0];
  }
}

async function confirmDelete() {
  const template = templateToDelete.value;
  templateToDelete.value = null;

  const result = await editor.removeSaved(template);

  if (!result.success) {
    toast('Não foi possível excluir o modelo', { type: TYPE.ERROR, timeout: 6000 });
  }
}

async function setDefault(enabled) {
  /* O InputSwitch emite o valor atual ao montar; só chama a API quando muda. */
  if (enabled === editor.isCurrentDefault.value) {
    return;
  }

  const result = await editor.setCurrentAsDefault(enabled);

  if (!result.success) {
    toast('Não foi possível definir o padrão', { type: TYPE.ERROR, timeout: 6000 });
  }
}
</script>

<style scoped>
.nested_modal {
  z-index: 100000 !important;
}

.generator_loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 480px;
}

.image_generator {
  display: grid;
  /*
   * Coluna do preview e altura fixas: Story e Publicação têm tamanhos
   * diferentes e, sem isso, o modal muda de tamanho ao trocar o formato.
   */
  grid-template-columns: 370px minmax(0, 1fr);
  min-height: 660px;
  gap: 30px;
  align-items: start;
  /* Encosta no cabeçalho e no rodapé para a divisória vertical encontrar as linhas deles. */
  margin-top: -20px;
}

.preview_column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding: 20px 30px 20px 0;
  border-right: 1px #d7d7d7 solid;
  align-self: stretch;
}

.dark .preview_column {
  border-color: #414556;
}

.format_buttons {
  display: flex;
  gap: 8px;
  align-self: stretch;
}

.format_buttons > * {
  flex: 1 1 0;
}

.preview_size {
  color: #707070;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 26px;
  max-height: 640px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 22px 2px 20px;
}

.option_group h6 {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
}

.dark .option_group h6 {
  color: #cbcfe9;
}

.option_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.option_header h6 {
  margin: 0;
}

.default_toggle {
  border: 0;
  padding: 4px 8px;
  margin-right: -8px;
  border-radius: 4px;
  background: transparent;
  color: #707070;
  font-size: 12px;
  font-weight: 500;
}

.default_toggle i {
  margin-right: 4px;
}

.default_toggle:hover {
  background-color: #f2f2f2;
  color: #2c3e50;
}

.default_toggle.active {
  color: #583a8e;
}

.default_toggle.active i {
  color: #f5b400;
}

.dark .default_toggle:hover {
  background-color: #3a414f;
  color: #cbcfe9;
}

.dark .default_toggle.active {
  color: #cbcfe9;
}

/* Faixa de modelos: rola para o lado, com esmaecido na borda indicando que há mais. */
.templates_wrap {
  position: relative;
}

.templates_wrap::before,
.templates_wrap::after {
  content: "";
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 10px;
  width: 36px;
  pointer-events: none;
}

.templates_wrap::after {
  right: 0;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), #fff);
}

/* Esmaecido da esquerda só aparece depois de rolar, para não apagar o primeiro modelo. */
.templates_wrap::before {
  left: 0;
  background: linear-gradient(to left, rgba(255, 255, 255, 0), #fff);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.templates_wrap.scrolled::before {
  opacity: 1;
}

.dark .templates_wrap::after {
  background: linear-gradient(to right, rgba(46, 51, 61, 0), #2e333d);
}

.dark .templates_wrap::before {
  background: linear-gradient(to left, rgba(46, 51, 61, 0), #2e333d);
}

.templates {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 3px 36px 10px 3px;
  scrollbar-width: thin;
  scrollbar-color: #d7d7d7 transparent;
}

.templates::-webkit-scrollbar {
  height: 4px;
}

.templates::-webkit-scrollbar-thumb {
  background-color: #d7d7d7;
  border-radius: 4px;
}

.template {
  position: relative;
  flex: 0 0 72px;
  cursor: pointer;
  text-align: center;
  outline: none;
}

.templates.post .template {
  flex-basis: 96px;
}

.template :deep(canvas) {
  outline: 2px solid transparent;
  outline-offset: 2px;
  border-radius: 4px;
}

.template:hover :deep(canvas) {
  outline-color: #d7d7d7;
}

.template.active :deep(canvas),
.template:focus-visible :deep(canvas) {
  outline-color: #583a8e;
}

.template_name {
  display: block;
  margin-top: 7px;
  font-size: 12px;
  color: #707070;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template.active .template_name {
  color: #583a8e;
  font-weight: 600;
}

.dark .template.active .template_name {
  color: #cbcfe9;
}

.template_default,
.template_delete {
  position: absolute;
  top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 4px;
  font-size: 10px;
  background-color: rgba(0, 0, 0, 0.6);
}

.template_default {
  left: 4px;
  color: #f5b400;
  pointer-events: none;
}

.template_delete {
  right: 4px;
  color: #fff;
  opacity: 0;
}

.template:hover .template_delete,
.template:focus-within .template_delete {
  opacity: 1;
}

@media (hover: none) {
  .template_delete {
    opacity: 1;
  }
}

/* Cores: fundo do modelo com o destaque no centro; tudo numa linha. */
.swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.swatch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  transition: transform 0.12s ease;
}

.swatch:hover {
  transform: translateY(-1px);
}

.swatch_accent {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.25);
}

.swatch.active,
.swatch:focus-visible {
  outline: 2px solid #583a8e;
  outline-offset: 2px;
}

.dark .swatch.active,
.dark .swatch:focus-visible {
  outline-color: #a98ae6;
}

/* Sem cor escolhida mostra o espectro; escolhida, mostra a própria cor. */
.swatch.custom {
  background-image: conic-gradient(#ff4d4d, #ffcf33, #45d16e, #33b5ff, #8f5cff, #ff4dc4, #ff4d4d);
}

.swatch.custom.active {
  background-image: none;
}

.swatch_icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.92);
  color: #2c3e50;
  font-size: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

.swatch.custom input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

/* Fontes: amostra "Aa" na própria fonte e nome embaixo, todas numa linha. */
.fonts {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
}

.font {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 72px;
  padding: 6px 4px;
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  background-color: #fff;
  color: #2c3e50;
  transition: border-color 0.12s ease, background-color 0.12s ease;
}

.font_sample {
  font-size: 24px;
  line-height: 1;
}

.font_name {
  max-width: 100%;
  font-size: 11px;
  font-weight: 500;
  color: #707070;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.font:hover {
  border-color: #b9a8d8;
}

.font.active {
  border-color: #583a8e;
  background-color: #f6f2fc;
  color: #583a8e;
  box-shadow: inset 0 0 0 1px #583a8e;
}

.font.active .font_name {
  color: #583a8e;
  font-weight: 600;
}

.font:focus-visible {
  outline: 2px solid #583a8e;
  outline-offset: 2px;
}

.dark .font {
  background-color: #333945;
  border-color: transparent;
  color: #e4e6f3;
}

.dark .font_name {
  color: #9a9fb8;
}

.dark .font:hover {
  border-color: #545b70;
}

.dark .font.active {
  background-color: #3a3252;
  border-color: #a98ae6;
  box-shadow: inset 0 0 0 1px #a98ae6;
  color: #fff;
}

.dark .font.active .font_name {
  color: #d9cdf5;
}

/* Chamada: interruptor e texto na mesma linha. */
.toggle_row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.toggle_row :deep(.input_group) {
  margin: 0;
}

.toggle_text {
  flex: 1;
  min-width: 0;
}

.toggle_text :deep(input) {
  width: 100%;
  min-width: 0;
}

.footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin: 0 -20px -20px;
  padding: 15px 20px;
  border-top: 1px #d7d7d7 solid;
}

.dark .footer {
  border-color: #414556;
}

.footer_actions {
  display: flex;
  gap: 8px;
}

.save_image {
  flex: 0 0 auto;
  padding-left: 16px;
  padding-right: 16px;
}

@media screen and (max-width: 860px) {
  .image_generator {
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
    gap: 0;
  }

  .preview_column {
    padding: 16px 0;
    border-right: 0;
    border-bottom: 1px #d7d7d7 solid;
  }

  .options {
    max-height: none;
    overflow: visible;
    gap: 18px;
    padding: 16px 0;
  }

  /* Modelos, cores e fontes rolam de borda a borda da tela (anula o padding de 20px do modal). */
  .templates_wrap::before,
  .templates_wrap::after {
    display: none;
  }

  .templates {
    margin: 0 -20px;
    padding: 3px 20px 10px;
    scroll-padding: 0 20px;
  }

  .swatches,
  .fonts {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    margin: 0 -20px;
    padding: 4px 20px 8px;
    scrollbar-width: none;
  }

  .swatches::-webkit-scrollbar,
  .fonts::-webkit-scrollbar {
    display: none;
  }

  .swatch,
  .font {
    flex: 0 0 auto;
  }

  .font {
    width: 76px;
  }

  .preview_size {
    display: none;
  }

  /* Ação principal sempre à vista no fim da tela. */
  .footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    margin-top: 0;
    padding: 12px 20px calc(12px + env(safe-area-inset-bottom));
    background-color: #fff;
  }

  .dark .footer {
    background-color: #2e333d;
  }

  .footer > :last-child {
    flex: 1;
  }

  .main_action {
    flex: 1;
  }
}
</style>
